import { TuettuKieli } from '@lemon/core/model/common'
import assertNever from '@lemon/core/utils/assert-never'
import { KirjanpidonPeruste, TyokieltoTapahtumaTyyppi, Yritysmuoto } from '../model/kayttaja'
import { TILITAPAHTUMAN_KANAVAT, TILITAPAHTUMAN_TILAT, TilitapahtumanKanava, TilitapahtumanTila } from '../model/tiliote'

export interface EnumOption<T> {
  value: T
  label: string
}

export const label = (kieli: TuettuKieli, kaannokset: Record<TuettuKieli, string>): string => {
  return kaannokset[kieli]
}

/**
 * Palauttaa datamallille (enum tai string literal union) optionit esimerkiksi select-komponenttia varten.
 *
 * Käyttö:
 *
 * **String literal union:**
 * 1. Määrittele const array ja tyyppi:
 *    ```ts
 *    export const TILITAPAHTUMAN_TILAT = ['final', 'unconfirmed'] as const
 *    export type TilitapahtumanTila = typeof TILITAPAHTUMAN_TILAT[number]
 *    ```
 *
 * 2. Lisää label-metodi ja options-metodi:
 *    ```ts
 *    tilitapahtumanTila(tila: TilitapahtumanTila, tuettuKieli: TuettuKieli): string {
 *      switch (tila) {
 *        case 'final': return label(tuettuKieli, { fi: 'Vahvistettu', en: 'Confirmed' })
 *        case 'unconfirmed': return label(tuettuKieli, { fi: 'Vahvistamaton', en: 'Unconfirmed' })
 *        default: return assertNever(tila)
 *      }
 *    }
 *
 *    tilitapahtumanTilaOptions(tuettuKieli: TuettuKieli): EnumOption<TilitapahtumanTila>[] {
 *      return TILITAPAHTUMAN_TILAT.map(tila => ({
 *        value: tila,
 *        label: this.tilitapahtumanTila(tila, tuettuKieli)
 *      }))
 *    }
 *    ```
 *
 * **Enum:**
 * 1. Määrittele enum:
 *    ```ts
 *    export enum BankBalanceCalcAlgorithm {
 *      AIIA_OLD = 'aiia-old',
 *      VERSION_ONE = 'v1'
 *    }
 *    ```
 *
 * 2. Lisää label-metodi ja options-metodi (käytä Object.values):
 *    ```ts
 *    bankBalanceAlgorithm(algo: BankBalanceCalcAlgorithm, tuettuKieli: TuettuKieli): string {
 *      switch (algo) {
 *        case BankBalanceCalcAlgorithm.AIIA_OLD: return label(tuettuKieli, { fi: 'Aiia (vanha)', en: 'Aiia (old)' })
 *        case BankBalanceCalcAlgorithm.VERSION_ONE: return label(tuettuKieli, { fi: 'Versio 1', en: 'Version 1' })
 *        default: return assertNever(algo)
 *      }
 *    }
 *
 *    bankBalanceAlgorithmOptions(tuettuKieli: TuettuKieli): EnumOption<BankBalanceCalcAlgorithm>[] {
 *      return Object.values(BankBalanceCalcAlgorithm).map(algo => ({
 *        value: algo,
 *        label: this.bankBalanceAlgorithm(algo, tuettuKieli)
 *      }))
 *    }
 *    ```
 */
export class OptionLabelsService {
  tilitapahtumanTila(tila: TilitapahtumanTila, tuettuKieli: TuettuKieli): string {
    switch (tila) {
      case 'final':
        return label(tuettuKieli, { fi: 'Vahvistettu', en: 'Confirmed' })
      case 'unconfirmed':
        return label(tuettuKieli, { fi: 'Vahvistamaton', en: 'Unconfirmed' })
      default:
        return assertNever(tila)
    }
  }

  tilitapahtumanTilaOptions(tuettuKieli: TuettuKieli): EnumOption<TilitapahtumanTila>[] {
    return TILITAPAHTUMAN_TILAT.map(tila => ({
      value: tila,
      label: this.tilitapahtumanTila(tila, tuettuKieli),
    }))
  }

  tilitapahtumanKanava(kanava: TilitapahtumanKanava, tuettuKieli: TuettuKieli): string {
    switch (kanava) {
      case 'aiia':
        return label(tuettuKieli, { fi: 'Aiia', en: 'Aiia' })
      case 'ws-camt':
        return label(tuettuKieli, { fi: 'WS-CAMT', en: 'WS-CAMT' })
      case 'holviapi':
        return label(tuettuKieli, { fi: 'Holvi API', en: 'Holvi API' })
      case 'nordigen':
        return label(tuettuKieli, { fi: 'Nordigen', en: 'Nordigen' })
      case 'enable-banking':
        return label(tuettuKieli, { fi: 'Enable Banking', en: 'Enable Banking' })
      default:
        return assertNever(kanava)
    }
  }

  tilitapahtumanKanavaOptions(tuettuKieli: TuettuKieli): EnumOption<TilitapahtumanKanava>[] {
    return TILITAPAHTUMAN_KANAVAT.map(kanava => ({
      value: kanava,
      label: this.tilitapahtumanKanava(kanava, tuettuKieli),
    }))
  }

  yritysmuoto(muoto: Yritysmuoto, tuettuKieli: TuettuKieli): string {
    switch (muoto) {
      case Yritysmuoto.TOIMINIMI:
        return label(tuettuKieli, { fi: 'Toiminimi', en: 'Sole trader' })
      case Yritysmuoto.AVOINYHTIO:
        return label(tuettuKieli, { fi: 'Avoin yhtiö', en: 'General partnership' })
      case Yritysmuoto.KOMMANDIITTIYHTIO:
        return label(tuettuKieli, { fi: 'Kommandiittiyhtiö', en: 'Limited partnership' })
      case Yritysmuoto.OSAKEYHTIO:
        return label(tuettuKieli, { fi: 'Osakeyhtiö', en: 'Limited company' })
      case Yritysmuoto.OSUUSKUNTA:
        return label(tuettuKieli, { fi: 'Osuuskunta', en: 'Cooperative' })
      case Yritysmuoto.YHDISTYS:
        return label(tuettuKieli, { fi: 'Yhdistys', en: 'Association' })
      default:
        return assertNever(muoto)
    }
  }

  yritysmuotoOptions(tuettuKieli: TuettuKieli): EnumOption<Yritysmuoto>[] {
    return Object.values(Yritysmuoto).map(muoto => ({
      value: muoto,
      label: this.yritysmuoto(muoto, tuettuKieli),
    }))
  }

  kirjanpidonPeruste(peruste: KirjanpidonPeruste, tuettuKieli: TuettuKieli): string {
    switch (peruste) {
      case KirjanpidonPeruste.LASKU:
        return label(tuettuKieli, { fi: 'Laskuperuste', en: 'Invoice basis' })
      case KirjanpidonPeruste.SUORITE:
        return label(tuettuKieli, { fi: 'Suoriteperuste', en: 'Accrual basis' })
      case KirjanpidonPeruste.MAKSU:
        return label(tuettuKieli, { fi: 'Maksuperuste', en: 'Cash basis' })
      default:
        return assertNever(peruste)
    }
  }

  kirjanpidonPerusteOptions(tuettuKieli: TuettuKieli): EnumOption<KirjanpidonPeruste>[] {
    return Object.values(KirjanpidonPeruste).map(peruste => ({
      value: peruste,
      label: this.kirjanpidonPeruste(peruste, tuettuKieli),
    }))
  }

  tyokieltoTapahtumaTyyppi(tyyppi: TyokieltoTapahtumaTyyppi): string {
    switch (tyyppi) {
      case 'asetettu':
        return 'Työkielto asetettu'
      case 'poistettu-manuaalisesti':
        return 'Työkielto poistettu manuaalisesti'
      case 'poistunut-automaattisesti':
        return 'Työkielto poistunut automaattisesti'
      default:
        return assertNever(tyyppi)
    }
  }
}
